/**
 * Eine Prüfung aus Sicht der Hausverwaltung.
 *
 * Alles, was der Beirat vorfinden soll, wird hier bereitgestellt: die
 * Belege (aus iDWELL oder dem Importpaket), die Unterlagen je Prüfschritt
 * unter den Menüpunkten des Mandanten, die Beiräte mit ihren Links. Dazu
 * die Freigabe und der Blick in die Beiratsansicht.
 *
 * Die Unterlagen stehen in der Reihenfolge der fünf Schritte — so sieht die
 * Verwaltung auf einen Blick, wo noch etwas fehlt, bevor der Beirat es
 * merkt.
 */

import { useCallback, useEffect, useState } from 'react';

import { api, datum, eur } from '../../lib/api';
import type { Dokument, Kategorie, Kennzahlen, Mandant, Mitglied, Pruefung, Schritt } from '../../lib/typen';
import { Ablagezone } from '../../komponenten/Ablagezone';
import { Fortschritt } from '../../komponenten/Fortschritt';
import { groesseText } from '../../komponenten/Dokumentansicht';
import { Einladen } from '../Einrichten';
import { Eingang } from './Eingang';

export function PruefungVerwalten({
  pruefungId,
  mandant,
  onZurueck,
  onOeffnen,
  onMeldung,
}: {
  pruefungId: string;
  mandant: Mandant;
  onZurueck: () => void;
  onOeffnen: () => void;
  onMeldung: (t: string) => void;
}) {
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [kennzahlen, setKennzahlen] = useState<Kennzahlen | null>(null);
  const [schritte, setSchritte] = useState<Schritt[]>([]);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [mitglieder, setMitglieder] = useState<Mitglied[]>([]);
  const [laedt, setLaedt] = useState<string | null>(null);
  const [frist, setFrist] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * Wie hochgeladen wird: alles auf einmal in den Eingang, oder je Menüpunkt
   * eine eigene Fläche. Eine Bequemlichkeit des Betrachters, im Browser
   * gemerkt — die Unterlagen selbst sind in beiden Ansichten dieselben.
   */
  const [modus, setModus] = useState<'gesamt' | 'einzeln'>(() => {
    try {
      return localStorage.getItem('bp_upload_modus') === 'einzeln' ? 'einzeln' : 'gesamt';
    } catch {
      return 'gesamt';
    }
  });
  function modusSetzen(m: 'gesamt' | 'einzeln') {
    setModus(m);
    try {
      localStorage.setItem('bp_upload_modus', m);
    } catch {
      // Nur eine Bequemlichkeit.
    }
  }

  const laden = useCallback(async () => {
    try {
      const [kopf, zahlen, s, d, m] = await Promise.all([
        api.pruefung(pruefungId),
        api.kennzahlen(pruefungId),
        api.schritte(pruefungId),
        api.dokumente(pruefungId),
        api.mandant.mitglieder(pruefungId).catch(() => [] as Mitglied[]),
      ]);
      setPruefung(kopf.pruefung);
      setKennzahlen(zahlen);
      setSchritte(s.schritte);
      setKategorien(d.kategorien.filter((k) => k.aktiv));
      setDokumente(d.dokumente);
      setMitglieder(m);
      setFrist(kopf.pruefung.frist?.slice(0, 10) ?? '');
      setFehler(null);
    } catch (g) {
      setFehler((g as Error).message);
    }
  }, [pruefungId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function hochladen(kategorieId: string, dateien: File[]) {
    setLaedt(kategorieId);
    try {
      const antwort = await api.dokumenteHochladen(pruefungId, kategorieId, dateien);
      setDokumente((alt) => [...alt, ...antwort.dokumente]);
      onMeldung(`${antwort.dokumente.length} ${antwort.dokumente.length === 1 ? 'Unterlage' : 'Unterlagen'} bereitgestellt.`);
      setKennzahlen(await api.kennzahlen(pruefungId));
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaedt(null);
    }
  }

  /** Welche Unterlage gerade bearbeitet wird (Name, Menüpunkt). */
  const [bearbeitung, setBearbeitung] = useState<{ id: string; dateiname: string; kategorieId: string } | null>(null);
  const [speichert, setSpeichert] = useState(false);

  async function aenderungSpeichern() {
    if (!bearbeitung) return;
    const d = dokumente.find((x) => x.id === bearbeitung.id);
    if (!d) return setBearbeitung(null);
    const aenderung: { dateiname?: string; kategorieId?: string } = {};
    if (bearbeitung.dateiname.trim() && bearbeitung.dateiname !== d.dateiname) aenderung.dateiname = bearbeitung.dateiname;
    if (bearbeitung.kategorieId !== d.kategorieId) aenderung.kategorieId = bearbeitung.kategorieId;
    if (!Object.keys(aenderung).length) return setBearbeitung(null);
    setSpeichert(true);
    try {
      const a = await api.dokumentAendern(pruefungId, d.id, aenderung);
      setDokumente((alt) => alt.map((x) => (x.id === d.id ? a.dokument : x)));
      setBearbeitung(null);
      if (aenderung.kategorieId) setKennzahlen(await api.kennzahlen(pruefungId));
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setSpeichert(false);
    }
  }

  /** Haken „für den Belegabgleich" an einem Kontoauszug. */
  const [abgleichLaeuft, setAbgleichLaeuft] = useState<string | null>(null);
  async function abgleichUmschalten(d: Dokument, aktiv: boolean) {
    setAbgleichLaeuft(d.id);
    try {
      const a = await api.dokumentAbgleich(pruefungId, d.id, aktiv);
      setDokumente((alt) => alt.map((x) => (x.id === d.id ? a.dokument : x)));
      onMeldung(a.meldung);
      setKennzahlen(await api.kennzahlen(pruefungId));
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setAbgleichLaeuft(null);
    }
  }

  const [kiLaeuft, setKiLaeuft] = useState<string | null>(null);
  async function kiAbgleichStarten(d: Dokument) {
    setKiLaeuft(d.id);
    onMeldung('KI-Abgleich läuft — das kann bei vielen Belegen eine Minute dauern …');
    try {
      const a = await api.dokumentKiAbgleich(pruefungId, d.id);
      onMeldung(a.meldung);
      setKennzahlen(await api.kennzahlen(pruefungId));
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setKiLaeuft(null);
    }
  }

  async function loeschen(d: Dokument) {
    if (!window.confirm(`„${d.dateiname}" entfernen? Der Beirat sieht sie dann nicht mehr.`)) return;
    try {
      await api.dokumentLoeschen(pruefungId, d.id);
      setDokumente((alt) => alt.filter((x) => x.id !== d.id));
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function importieren(dateien: File[]) {
    const datei = dateien[0];
    if (!datei) return;
    setLaedt('import');
    onMeldung('Importpaket wird gelesen …');
    try {
      const a = await api.importHochladen(pruefungId, datei);
      onMeldung(
        `${a.belege} Belege (${a.mitDatei} mit Datei) und ${a.buchungen} Buchungen eingespielt${
          a.fehlendeDateien ? ` — ${a.fehlendeDateien} Belegdateien fehlen im Paket` : ''
        }.`,
      );
      await laden();
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaedt(null);
    }
  }

  async function belegeLaden() {
    setLaedt('belege');
    try {
      const a = await api.belegeLaden(pruefungId);
      onMeldung(`${a.geladen} Belege aus iDWELL geladen.`);
      await laden();
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaedt(null);
    }
  }

  async function freigeben() {
    setLaedt('freigabe');
    try {
      await api.freigeben(pruefungId, frist || null);
      await laden();
      onMeldung('Prüfung freigegeben — der Beirat kann beginnen.');
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaedt(null);
    }
  }

  async function mitgliedEntfernen(m: Mitglied) {
    if (!window.confirm(`Den Zugang von ${m.name} entfernen? Der Link funktioniert dann nicht mehr.`)) return;
    try {
      await api.mandant.mitgliedEntfernen(pruefungId, m.id);
      setMitglieder((alt) => alt.filter((x) => x.id !== m.id));
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  if (fehler) {
    return (
      <div className="wrap">
        <div className="leer card">
          <h3>Prüfung nicht erreichbar</h3>
          <p>{fehler}</p>
          <button className="btn" onClick={onZurueck}>Zurück</button>
        </div>
      </div>
    );
  }
  if (!pruefung) return <div className="wrap"><div className="skelett" style={{ height: 200 }} /></div>;

  const beiraete = mitglieder.filter((m) => m.rolle === 'beirat');
  const istImport = pruefung.belegquelle === 'import';
  const abgeschlossen = pruefung.status === 'abgeschlossen';

  return (
    <div className="wrap">
      <div className="schritt-pfad">
        <button className="btn ghost sm" onClick={onZurueck}>← Prüfungen</button>
      </div>
      <div className="seitenkopf">
        <div>
          <div className="eyebrow">{pruefung.liegenschaft_bez}</div>
          <h2 style={{ margin: 0 }}>
            {pruefung.bezeichnung} · {datum(pruefung.periode_von)} – {datum(pruefung.periode_bis)}
          </h2>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className={`pill ${abgeschlossen ? 'ok' : pruefung.status === 'freigegeben' ? 'n' : 'w'}`}>
              <span className="dot" />
              {abgeschlossen ? 'Abgeschlossen' : pruefung.status === 'freigegeben' ? 'Freigegeben' : 'In Vorbereitung'}
            </span>
            <span className="chip">{istImport ? 'Belege aus Importpaket' : 'Belege aus iDWELL'}</span>
            {kennzahlen && kennzahlen.rueckfragenOffen > 0 && (
              <span className="badge w">{kennzahlen.rueckfragenOffen} offene Rückfrage{kennzahlen.rueckfragenOffen === 1 ? '' : 'n'}</span>
            )}
          </div>
        </div>
        <div className="spacer" />
        <button className="btn" onClick={onOeffnen}>
          Beiratsansicht öffnen →
        </button>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 18 }}>
        <Fortschritt anteil={kennzahlen?.fortschritt ?? 0} text={`${kennzahlen?.schritteErledigt ?? 0} von ${schritte.length} Prüfbereichen vom Beirat abgeschlossen`} gross />
        <div className="statusband" style={{ padding: 0, border: 0, background: 'none', marginTop: 12 }}>
          <Zahl wert={String(kennzahlen?.belegeGesamt ?? 0)} label="Belege" />
          <Zahl wert={eur(kennzahlen?.pruefsumme ?? 0)} label="Prüfsumme" />
          <Zahl wert={String(dokumente.length)} label="Unterlagen" />
          <Zahl wert={String(beiraete.length)} label="Beiräte" />
          <Zahl wert={String(kennzahlen?.rueckfragenOffen ?? 0)} label="Offene Rückfragen" ton={kennzahlen?.rueckfragenOffen ? 'w' : undefined} />
        </div>
      </div>

      {/* ── Hochladen: alles auf einmal oder je Menüpunkt ── */}
      {!abgeschlossen && (
        <div className="seitenkopf" style={{ marginTop: 4 }}>
          <h3 className="sec" style={{ margin: 0 }}>Unterlagen hochladen</h3>
          <div className="spacer" />
          <div className="seg" role="group" aria-label="Art des Hochladens">
            <button aria-pressed={modus === 'gesamt'} onClick={() => modusSetzen('gesamt')}>
              Alles auf einmal
            </button>
            <button aria-pressed={modus === 'einzeln'} onClick={() => modusSetzen('einzeln')}>
              Je Menüpunkt
            </button>
          </div>
        </div>
      )}
      {!abgeschlossen && modus === 'gesamt' && <Eingang pruefung={pruefung} onMeldung={onMeldung} onUebernommen={laden} />}

      {/* ── Belege ───────────────────────────────────────── */}
      <h3 className="sec">Belege und Buchungen</h3>
      <div className="card" style={{ padding: 16 }}>
        {istImport ? (
          <>
            <p className="s" style={{ marginTop: 0 }}>
              Laden Sie den Export Ihrer Verwaltungssoftware als ZIP hoch: die Buchungsliste (CSV, „Buchungen nach Konten")
              zusammen mit dem Belegordner. Daraus entstehen die Belege für Schritt 3 und das Journal. Ein neues Paket ersetzt das alte.
            </p>
            {kennzahlen && kennzahlen.belegeGesamt > 0 && (
              <p className="note">
                Eingespielt: {kennzahlen.belegeGesamt} Belege, {kennzahlen.journalZeilen} Buchungen
                {kennzahlen.journalBefunde ? `, ${kennzahlen.journalBefunde} Befunde im Abgleich` : ''}.
              </p>
            )}
            {modus === 'einzeln' || abgeschlossen ? (
              <Ablagezone
                endungen={['.zip', '.csv']}
                titel={laedt === 'import' ? 'Wird eingespielt …' : 'Importpaket hier ablegen'}
                hinweis="ZIP mit Buchungsliste und Belegordner — oder nur die CSV"
                onDateien={importieren}
              />
            ) : (
              <p className="s" style={{ margin: 0 }}>Das Importpaket legen Sie oben in den Eingang — es wird als solches erkannt.</p>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <b>{kennzahlen?.belegeGesamt ?? 0} Belege aus iDWELL</b>
              <p className="s" style={{ margin: '2px 0 0' }}>
                Nach Rechnungsdatum im Abrechnungsjahr. Neu laden holt Belege, die seit dem Anlegen in iDWELL dazugekommen sind.
              </p>
            </div>
            <button className="btn sm" onClick={belegeLaden} disabled={laedt === 'belege' || !mandant.idwellVerbunden}>
              {laedt === 'belege' ? 'Lädt …' : 'Belege neu laden'}
            </button>
          </div>
        )}
        <p className="note" style={{ marginBottom: 0 }}>
          Kontoauszüge (CAMT, MT940, CSV, PDF) und das Journal legen Sie ebenfalls in den Eingang — sie werden erkannt und für den Zahlungsabgleich gelesen. Die Befunde sehen Sie in der Beiratsansicht unter Schritt 3.
        </p>
      </div>

      {/* ── Unterlagen je Schritt ─────────────────────────── */}
      <div className="seitenkopf" style={{ marginTop: 22 }}>
        <h3 className="sec" style={{ margin: 0 }}>Unterlagen je Prüfschritt</h3>
        <div className="spacer" />
      </div>
      <p className="s" style={{ marginTop: 0 }}>
        {modus === 'einzeln'
          ? 'Die Menüpunkte stammen aus Ihren Einstellungen. PDF, Bilder, CSV und Excel; mehrere Dateien je Punkt sind möglich.'
          : 'Was bereits bereitgestellt ist, je Prüfschritt. Hochgeladen wird oben im Eingang; Menüpunkte lassen sich in den Einstellungen ändern.'}
      </p>

      {schritte.map((s) => {
        const kat = kategorien.filter((k) => k.schritt === s.nummer);
        return (
          <div key={s.nummer} className="card" style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <div className={`schritt-nummer ${s.status}`} aria-hidden="true">{s.status === 'erledigt' ? '✓' : s.nummer}</div>
              <div>
                <b>{s.titel}</b>
                <div className="s" style={{ margin: 0 }}>{s.kurz}</div>
              </div>
            </div>
            {kat.length === 0 && <p className="s">Keine Menüpunkte für diesen Schritt — in den Einstellungen anlegen.</p>}
            {kat.map((k) => {
              const doks = dokumente.filter((d) => d.kategorieId === k.id);
              return (
                <div key={k.id} className="kategorie-block">
                  <div className="kategorie-kopf">
                    <b>{k.bezeichnung}</b>
                    {k.hinweis && <span className="s" style={{ margin: 0 }}>{k.hinweis}</span>}
                  </div>
                  {doks.map((d) =>
                    bearbeitung?.id === d.id ? (
                      <form
                        key={d.id}
                        className="dok-zeile dok-bearbeiten"
                        onSubmit={(ev) => {
                          ev.preventDefault();
                          void aenderungSpeichern();
                        }}
                      >
                        <input
                          className="dok-name-feld"
                          value={bearbeitung.dateiname}
                          onChange={(ev) => setBearbeitung({ ...bearbeitung, dateiname: ev.target.value })}
                          aria-label="Name der Unterlage"
                          autoFocus
                          spellCheck={false}
                        />
                        <select
                          value={bearbeitung.kategorieId}
                          onChange={(ev) => setBearbeitung({ ...bearbeitung, kategorieId: ev.target.value })}
                          aria-label="Menüpunkt"
                        >
                          {schritte.map((sx) => (
                            <optgroup key={sx.nummer} label={`Schritt ${sx.nummer} · ${sx.titel}`}>
                              {kategorien
                                .filter((kx) => kx.schritt === sx.nummer)
                                .map((kx) => (
                                  <option key={kx.id} value={kx.id}>
                                    {kx.bezeichnung}
                                  </option>
                                ))}
                            </optgroup>
                          ))}
                        </select>
                        <button className="btn sm" type="submit" disabled={speichert}>
                          {speichert ? 'Speichert …' : 'Speichern'}
                        </button>
                        <button className="btn ghost sm" type="button" onClick={() => setBearbeitung(null)}>
                          Abbrechen
                        </button>
                      </form>
                    ) : (
                    <div key={d.id} className="dok-zeile">
                      <span className="dok-name">{d.dateiname}</span>
                      <span className="s" style={{ margin: 0 }}>
                        {d.seiten ? `${d.seiten} S. · ` : ''}{groesseText(d.groesse)} · {datum(d.hochgeladenAm)}
                      </span>
                      <span className={`chip${d.pruefstatus === 'geprueft' ? ' ok' : d.pruefstatus === 'rueckfrage' ? ' warn' : ''}`}>
                        {d.pruefstatus === 'geprueft' ? 'vom Beirat geprüft' : d.pruefstatus === 'rueckfrage' ? 'Rückfrage' : 'offen'}
                      </span>
                      {k.schluessel.startsWith('kontoauszug') && d.typ === 'application/pdf' && (
                        <label className="abgleich-haken" title="Die Kontobewegungen dieses Auszugs für den Zahlungsabgleich der Belege einlesen">
                          <input
                            type="checkbox"
                            checked={Boolean(d.abgleichQuelle)}
                            disabled={abgeschlossen || abgleichLaeuft === d.id}
                            onChange={(ev) => abgleichUmschalten(d, ev.target.checked)}
                          />
                          <span>
                            {abgleichLaeuft === d.id
                              ? 'Wird gelesen …'
                              : d.abgleichQuelle
                                ? `Belegabgleich · ${d.abgleichAnzahl ?? 0} Kontobewegungen`
                                : 'Für den Belegabgleich'}
                          </span>
                        </label>
                      )}
                      {k.schluessel.startsWith('kontoauszug') && d.typ === 'application/pdf' && d.abgleichQuelle && !abgeschlossen && (
                        <button
                          className="btn ghost sm"
                          onClick={() => kiAbgleichStarten(d)}
                          disabled={kiLaeuft === d.id}
                          title="Alle Belege ohne Zahlung mit dem Sprachmodell im Auszug suchen"
                        >
                          {kiLaeuft === d.id ? 'KI-Abgleich läuft …' : 'KI-Abgleich'}
                        </button>
                      )}
                      <a className="btn ghost sm" href={api.dokumentUrl(pruefungId, d.id)} target="_blank" rel="noreferrer">
                        Ansehen
                      </a>
                      {!abgeschlossen && (
                        <>
                          <button
                            className="btn ghost sm"
                            onClick={() => setBearbeitung({ id: d.id, dateiname: d.dateiname, kategorieId: d.kategorieId })}
                            title="Umbenennen oder einem anderen Menüpunkt zuordnen"
                          >
                            Bearbeiten
                          </button>
                          <button className="btn ghost sm" onClick={() => loeschen(d)}>
                            Entfernen
                          </button>
                        </>
                      )}
                    </div>
                    ),
                  )}
                  {!abgeschlossen && modus === 'einzeln' && (
                    <Ablagezone
                      endungen={['.pdf', '.png', '.jpg', '.jpeg', '.csv', '.xlsx', '.xls']}
                      mehrfach
                      titel={laedt === k.id ? 'Wird hochgeladen …' : doks.length ? 'Weitere Datei ablegen' : 'Datei hier ablegen oder auswählen'}
                      hinweis="PDF, PNG, JPG, CSV, XLSX"
                      onDateien={(dateien) => hochladen(k.id, dateien)}
                    />
                  )}
                  {!abgeschlossen && modus === 'gesamt' && doks.length === 0 && (
                    <div className="s" style={{ margin: '2px 0 0', color: 'var(--tx-3)' }}>Noch nichts bereitgestellt — oben in den Eingang legen.</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* ── Beiräte ───────────────────────────────────────── */}
      <h3 className="sec" style={{ marginTop: 22 }}>Beiräte und Freigabe</h3>
      <div className="card" style={{ padding: 16 }}>
        {beiraete.length === 0 ? (
          <p className="s" style={{ marginTop: 0 }}>Noch kein Beirat eingeladen.</p>
        ) : (
          <table className="schlicht">
            <tbody>
              {beiraete.map((m) => (
                <tr key={m.id}>
                  <td><b>{m.name}</b></td>
                  <td className="s">{m.email}</td>
                  <td className="s">{m.letzterZugriffAm ? `zuletzt ${datum(m.letzterZugriffAm)}` : 'noch nicht geöffnet'}</td>
                  <td className="s">Link gültig bis {datum(m.gueltigBis)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!abgeschlossen && (
                      <button className="btn ghost sm" onClick={() => mitgliedEntfernen(m)}>Entfernen</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="note">
          Die Links sind nach dem Erzeugen nicht mehr abrufbar. Wer seinen verloren hat, bekommt mit einer neuen Einladung einen neuen.
        </p>
      </div>
      {!abgeschlossen && (
        <Einladen pruefungId={pruefungId} liegenschaft={pruefung.liegenschaft_bez} jahr={Number(pruefung.periode_von.slice(0, 4))} vorbelegt={null} onEingeladen={laden} />
      )}

      {pruefung.status === 'vorbereitung' && (
        <div className="card" style={{ padding: 16, marginTop: 14, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="feld" style={{ marginBottom: 0 }}>
            <span>Prüffrist (optional)</span>
            <input type="date" value={frist} onChange={(e) => setFrist(e.target.value)} />
          </label>
          <button className="btn primary" onClick={freigeben} disabled={laedt === 'freigabe' || beiraete.length === 0}>
            Prüfung freigeben
          </button>
          <span className="s" style={{ margin: 0 }}>
            {beiraete.length === 0 ? 'Zuerst einen Beirat einladen.' : 'Danach sieht der Beirat die Prüfung als freigegeben; Unterlagen lassen sich weiterhin nachreichen.'}
          </span>
        </div>
      )}
    </div>
  );
}

function Zahl({ wert, label, ton }: { wert: string; label: string; ton?: 'w' }) {
  return (
    <div className="kpi">
      <div className={`v${ton ? ` ${ton}` : ''}`}>{wert}</div>
      <div className="l">{label}</div>
    </div>
  );
}
